/**
 * native.js — Web Platform API bridges
 *
 * Progressive-enhancement wrappers around:
 *   • Vibration API  (haptic feedback on Android Chrome; silently no-ops on iOS/desktop)
 *   • Web Share API  (native share sheet on iOS Safari, Android Chrome, and desktop where supported)
 *
 * All exported functions degrade silently on unsupported browsers.
 * None of these functions throw — errors are swallowed internally.
 */

// ── Haptic feedback ────────────────────────────────────────────────────────────

/** Short light tap — use on low-weight interactions (ratings, toggles). */
export function hapticLight() {
  try { navigator.vibrate?.(10) } catch {}
}

/** Medium pulse — use on action confirmations (More Like This, launch). */
export function hapticMedium() {
  try { navigator.vibrate?.([15, 8, 15]) } catch {}
}

/** Heavy single buzz — use on significant state changes. */
export function hapticHeavy() {
  try { navigator.vibrate?.(40) } catch {}
}

// ── Web Share API ──────────────────────────────────────────────────────────────

/**
 * canShare(data?) → boolean
 *
 * Returns true if the current browser supports Web Share API for the given
 * data shape. Safe to call during render to conditionally show the share button.
 */
export function canShare(data) {
  if (typeof navigator === 'undefined' || !navigator.share) return false
  if (data && navigator.canShare) return navigator.canShare(data)
  return true
}

/**
 * shareTitle(rec) → Promise<boolean>
 *
 * Invokes the native Share Sheet for a recommendation card.
 * Shares: title name, one-line reason blurb, and a deep-link to the title page.
 *
 * Returns true if the share was initiated (user may still cancel in the sheet).
 * Returns false if the API is unsupported or the user cancelled (AbortError).
 */
export async function shareTitle(rec) {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const url    = `${origin}/title/${encodeURIComponent(rec.title)}`
  const data   = {
    title: `${rec.title}${rec.year ? ` (${rec.year})` : ''}`,
    text:  rec.reason || rec.overview || `Check out ${rec.title} — recommended by Flow`,
    url,
  }
  if (!canShare(data)) return false
  try {
    await navigator.share(data)
    return true
  } catch (e) {
    if (e.name !== 'AbortError') console.warn('[share] failed:', e.message)
    return false
  }
}
